import { createContext, useContext } from 'react';

export interface ConfirmOptions {
  /** Dialog title */
  title: string;
  /** Dialog description */
  description: string;
  /** Confirm button text (default: "Bestätigen") */
  confirmText?: string;
  /** Cancel button text (default: "Abbrechen") */
  cancelText?: string;
  /** Button variant for confirm button (default: "default") */
  variant?: 'default' | 'destructive';
}

export interface ConfirmContextValue {
  /** Show confirmation dialog and wait for user response */
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

export const ConfirmContext = createContext<ConfirmContextValue | null>(null);

/**
 * Hook for showing confirmation dialogs
 *
 * Must be used within a ConfirmProvider.
 *
 * @example
 * ```tsx
 * const { confirm } = useConfirm();
 *
 * const handleDelete = async () => {
 *   const confirmed = await confirm({
 *     title: 'Eintrag löschen',
 *     description: 'Möchten Sie diesen Eintrag wirklich löschen?',
 *     confirmText: 'Löschen',
 *     variant: 'destructive',
 *   });
 *
 *   if (confirmed) {
 *     await deleteEntry();
 *   }
 * };
 * ```
 */
export function useConfirm(): ConfirmContextValue {
  const context = useContext(ConfirmContext);

  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }

  return context;
}
